<template>
  <div class="login-wrapper">
    <!-- Password Change Modal -->
    <ChangePasswordModal 
      :show="showPasswordModal" 
      @success="handlePasswordChanged"
    />
    
    <!-- Floating Language Switcher -->
    <div class="floating-language-switcher">
      <button 
        @click="switchLanguage('sv')" 
        class="float-lang-btn"
        :class="{ active: currentLocale === 'sv' }"
        title="Svenska"
      >
        🇸🇪
      </button>
      <button 
        @click="switchLanguage('en')" 
        class="float-lang-btn"
        :class="{ active: currentLocale === 'en' }"
        title="English"
      >
        🇬🇧
      </button>
    </div>

    <div class="login-container">
      <div class="login-header">
        <div class="logo">
          <img src="/rosterOwl1ico.png" alt="Shift Planner" class="logo-image" />
        </div>
        <h1 class="page-title login-title-diff">Roster OWL</h1>
        <p class="subtitle">{{ $t('auth.welcomeBack') }}</p>
      </div>
      
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="username" class="form-label">{{ $t('auth.username') }}</label>
          <div class="input-wrapper">
            <svg class="input-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            </svg>
            <input 
              v-model="username" 
              id="username" 
              type="text" 
              required 
              class="form-input"
              :placeholder="$t('auth.username')"
            />
          </div>
        </div>
        
        <div class="form-group">
          <label for="password" class="form-label">{{ $t('auth.password') }}</label>
          <div class="input-wrapper">
            <svg class="input-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
            </svg>
            <input 
              v-model="password" 
              id="password" 
              type="password" 
              required 
              class="form-input"
              :placeholder="$t('auth.password')"
            />
          </div>
        </div>
        
        <button type="submit" :disabled="loading" class="login-button">
          <span v-if="loading" class="loading-spinner"></span>
          <span v-if="!loading">{{ $t('auth.loginButton') }}</span>
          <span v-else>{{ $t('auth.loggingIn') }}</span>
        </button>
        
        <div v-if="loginStatus && loading" class="status-message">
          <div class="status-icon">🔗</div>
          {{ loginStatus }}
        </div>
        
        <div v-if="error" class="error-message">
          <svg class="error-icon" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ error }}
        </div>
        
        <!--<div class="login-switcher">
          <p class="switcher-text">Company administrator?</p>
          <router-link to="/saas/login" class="switcher-link">
            <span class="switcher-icon">🏢</span>
            Access Company Management
          </router-link>
        </div>-->
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import ChangePasswordModal from './ChangePasswordModal.vue'

const { t, locale } = useI18n()
const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')
const loginStatus = ref('')
const showPasswordModal = ref(false)
const router = useRouter()
const authStore = useAuthStore()

const currentLocale = computed(() => locale.value)

function switchLanguage(lang) {
  locale.value = lang
  localStorage.setItem('language', lang)
}

async function handlePasswordChanged() {
  // Password changed successfully - refresh user data and redirect
  showPasswordModal.value = false
  
  try {
    const userResponse = await authStore.makeAuthenticatedRequest('/api/users/me/')
    const userData = await userResponse.json()
    authStore.setUser(userData)
    
    // Redirect based on role
    if (userData.is_superuser) {
      router.push('/saas/scheduling')
    } else if (userData.role === 'manager') {
      router.push('/manager')
    } else {
      router.push('/dashboard')
    }
  } catch (e) {
    error.value = 'Failed to refresh user data. Please login again.'
  }
}

async function handleLogin() {
  loading.value = true
  error.value = ''
  loginStatus.value = 'Connecting to AI Scheduler...'
  
  try {
    // Use standard JWT authentication for tenant system
    const response = await fetch('/api/token/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.value,
        password: password.value
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.detail || 'Invalid credentials')
    }

    const tokenData = await response.json()
    
    // Set tokens in auth store
    authStore.setTokens(tokenData.access, tokenData.refresh)
    
    // Fetch user data
    const userResponse = await authStore.makeAuthenticatedRequest('/api/users/me/')
    const userData = await userResponse.json()
    authStore.setUser(userData)
    
    // Check if user must change password
    if (userData.must_change_password) {
      loginStatus.value = ''
      loading.value = false
      showPasswordModal.value = true
      return
    }
    
    loginStatus.value = '🤖 Connected to AI Scheduler!'
    
    // Brief delay to show status, then redirect
    setTimeout(() => {
      loginStatus.value = 'Redirecting...'
    }, 500)
    
    // Tenant system redirection logic
    setTimeout(() => {
      if (userData.is_superuser) {
        router.push('/saas/scheduling')
      } else if (userData.role === 'manager') {
        router.push('/manager')
      } else {
        router.push('/dashboard')
      }
    }, 800)
    
  } catch (e) {
    console.error('Tenant login failed:', e)
    error.value = e.message || 'Login failed. Please check your credentials.'
    loginStatus.value = '' // Clear status on error
  } finally {
    loading.value = false
    // Clear status after a delay
    setTimeout(() => {
      loginStatus.value = ''
    }, 3000)
  }
}
</script>

<style scoped>
.login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: 
    repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 30px,
      rgba(255, 255, 255, 0.03) 30px,
      rgba(255, 255, 255, 0.03) 60px
    ),
    linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
  position: relative;
}

/* Floating Language Switcher */
.floating-language-switcher {
  position: fixed;
  top: 2rem;
  right: 2rem;
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.float-lang-btn {
  width: 48px;
  height: 48px;
  border: 2px solid transparent;
  background: transparent;
  font-size: 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.float-lang-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  border-color: rgba(102, 126, 234, 0.3);
  transform: scale(1.05);
}

.float-lang-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .floating-language-switcher {
    top: 1rem;
    right: 1rem;
    padding: 0.4rem;
  }
  
  .float-lang-btn {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}

.login-container {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo {
  margin-bottom: 1rem;
}

.logo-image {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.login-title-diff {
  font-size: 2rem;
  color: #601ab1;
  }

.login-subtitle {
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 400;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
  margin-left: 0.25rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 1rem;
  width: 1.25rem;
  height: 1.25rem;
  color: #a0aec0;
  z-index: 1;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.form-input::placeholder {
  color: #a0aec0;
}

.login-button {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.status-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(72, 187, 120, 0.1);
  border: 1px solid #9ae6b4;
  border-radius: 12px;
  color: #2f855a;
  font-size: 0.875rem;
  font-weight: 500;
  animation: fadeIn 0.3s ease-in;
}

.status-icon {
  font-size: 1.25rem;
  animation: pulse 2s infinite;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(254, 178, 178, 0.1);
  border: 1px solid #fed7d7;
  border-radius: 12px;
  color: #c53030;
  font-size: 0.875rem;
  font-weight: 500;
}

.error-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.login-switcher {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.switcher-text {
  margin: 0 0 0.75rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.switcher-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #ff7043;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.switcher-link:hover {
  background: rgba(255, 112, 67, 0.1);
  transform: translateY(-1px);
}

.switcher-icon {
  font-size: 1rem;
}

/* Responsive design */
@media (max-width: 480px) {
  .login-wrapper {
    padding: 0.5rem;
  }
  
  .login-container {
    padding: 2rem 1.5rem;
  }
  
  .login-title {
    font-size: 1.75rem;
  }
}

/* Animation for container entrance */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-container {
  animation: fadeInUp 0.6s ease-out;
}
</style>
